// WP-1.16/WP-5.5 (OB-3, LG-4, S-30 `/health`): Data Health — sections per check with a count
// badge, expandable rows, and a real guided-fix action per soft-warning row (WP-1.16's own
// v1 was a drill-link only; WP-5.5 makes it "complete" per DEV_PLAN). Three sections get an
// inline mutation, each reusing an EXISTING engine-backed hook rather than a new write path:
// duplicate_suspect -> useReverseTransaction, stale_in_transit -> useSettleTransfer,
// undated_receivable -> useEditRpDueDate (new, WP-5.5's own genuinely-missing engine method).
// Hard-invariant sections and negative_balance keep the plain drill-link — an LG-4 violation
// is a bug to investigate, not a one-click fix, and a negative balance's real fix IS the
// Reconciliation wizard (S-13) this already links to. Two maintenance actions (OB-3/LG-4):
// "Rebuild projections" is a read-only replay-vs-live diagnostic (see routes/system.ts's own
// header comment for why it's not a live state swap); "Re-run invariants" is a plain refetch
// of this same GET, which already recomputes everything fresh on every call.
import { parseAmountInput } from '@pfos/engine';
import type { DataHealthRow, DataHealthSection, TransferResponse } from '@pfos/shared';
import { useQueryClient } from '@tanstack/react-query';
import { CircleCheck, ShieldAlert, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  AmountField,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
} from '../components/FormKit';
import { DATA_HEALTH_KEY, useDataHealth } from '../lib/health';
import { useEditRpDueDate } from '../lib/rps';
import { useRebuildProjections } from '../lib/system';
import { useReverseTransaction } from '../lib/transactions';
import { useSettleTransfer, useTransfers } from '../lib/transfers';

function fixHref(row: DataHealthRow): string | undefined {
  switch (row.entityType) {
    case 'transaction':
      return `/tx/${row.entityId}`;
    case 'transfer':
      return '/tx';
    case 'account':
      return `/accounts/${row.entityId}`;
    case 'rp':
      return '/owed';
    case 'tpf':
      return `/tpf/${row.entityId}`;
  }
}

function DuplicateSuspectFix({
  row,
  onFixed,
}: {
  row: DataHealthRow;
  onFixed: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const reverseMutation = useReverseTransaction(row.entityId);
  return (
    <>
      <button
        type="button"
        className="shrink-0 text-sm text-danger hover:underline"
        onClick={() => {
          setOpen(true);
        }}
      >
        Reverse
      </button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Reverse this transaction?"
        consequence="This was flagged as a possible duplicate. Reversing creates a reversal event; the original stays in your history (LG-2)."
        confirmLabel="Reverse"
        requireReason
        reasonLabel="Reason"
        confirming={reverseMutation.isPending}
        onConfirm={(reason) => {
          reverseMutation.mutate(reason, {
            onSuccess: () => {
              setOpen(false);
              onFixed();
            },
          });
        }}
      />
    </>
  );
}

function StaleTransferFix({
  row,
  transfer,
  onFixed,
}: {
  row: DataHealthRow;
  transfer: TransferResponse | undefined;
  onFixed: () => void;
}): React.JSX.Element | null {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const settleMutation = useSettleTransfer(row.entityId);
  if (transfer === undefined) return null; // resolved/settled between the health check and this render
  return (
    <div className="flex shrink-0 items-center gap-2">
      <AmountField
        id={`health-settle-${row.entityId}`}
        ariaLabel="Amount received"
        currency={transfer.sentCurrency}
        value={amount}
        onChange={setAmount}
      />
      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={settleMutation.isPending}
        onClick={() => {
          const parsed = parseAmountInput(amount, { defaultCurrency: transfer.sentCurrency });
          if (!parsed.ok) {
            setError('Enter a valid amount');
            return;
          }
          setError(null);
          settleMutation.mutate(parsed.money.minorUnits, {
            onSuccess: onFixed,
            onError: (e) => {
              setError(e.message);
            },
          });
        }}
      >
        Settle
      </button>
      {error !== null && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

function UndatedReceivableFix({
  row,
  onFixed,
}: {
  row: DataHealthRow;
  onFixed: () => void;
}): React.JSX.Element {
  const [date, setDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const editMutation = useEditRpDueDate(row.entityId);
  return (
    <div className="flex shrink-0 items-center gap-2">
      <input
        type="date"
        aria-label="Due date"
        className={inputClass}
        value={date}
        onChange={(e) => {
          setDate(e.target.value);
        }}
      />
      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={date === '' || editMutation.isPending}
        onClick={() => {
          const parsedMs = Date.parse(date);
          if (Number.isNaN(parsedMs)) {
            setError('Enter a valid date');
            return;
          }
          setError(null);
          editMutation.mutate(new Date(parsedMs).toISOString(), {
            onSuccess: onFixed,
            onError: (e) => {
              setError(e.message);
            },
          });
        }}
      >
        Set date
      </button>
      {error !== null && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

function Section({
  section,
  transfersById,
  onFixed,
}: {
  section: DataHealthSection;
  transfersById: Map<string, TransferResponse>;
  onFixed: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(section.severity === 'error'); // errors expanded by default
  const isError = section.severity === 'error';
  return (
    <div className="mb-3 rounded-[var(--radius-card)] border border-border bg-surface">
      <button
        type="button"
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <span className="flex items-center gap-2">
          {isError ? (
            <ShieldAlert size={18} className="text-danger" />
          ) : (
            <TriangleAlert size={18} className="text-warning" />
          )}
          <span className="text-sm font-medium text-ink">{section.title}</span>
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            isError ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'
          }`}
        >
          {section.rows.length}
        </span>
      </button>
      {open && (
        <ul className="border-t border-border px-4">
          {section.rows.map((row) => {
            const href = fixHref(row);
            return (
              <li
                key={`${row.entityType}-${row.entityId}`}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-2 last:border-0"
              >
                <span className="text-sm text-muted">{row.detail}</span>
                <div className="flex shrink-0 items-center gap-3">
                  {section.id === 'duplicate_suspect' && (
                    <DuplicateSuspectFix row={row} onFixed={onFixed} />
                  )}
                  {section.id === 'stale_in_transit' && (
                    <StaleTransferFix
                      row={row}
                      transfer={transfersById.get(row.entityId)}
                      onFixed={onFixed}
                    />
                  )}
                  {section.id === 'undated_receivable' && (
                    <UndatedReceivableFix row={row} onFixed={onFixed} />
                  )}
                  {href !== undefined && (
                    <Link to={href} className="text-sm text-primary hover:underline">
                      Review
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function DataHealthPage(): React.JSX.Element {
  const { data: report, isLoading, isError, isFetching, refetch } = useDataHealth();
  const { data: inTransitTransfers } = useTransfers({ status: 'in_transit' });
  const transfersById = useMemo(
    () => new Map((inTransitTransfers ?? []).map((t) => [t.id, t])),
    [inTransitTransfers],
  );
  const queryClient = useQueryClient();
  const onFixed = (): void => {
    void queryClient.invalidateQueries({ queryKey: DATA_HEALTH_KEY });
  };
  const rebuildMutation = useRebuildProjections();
  const [rebuildResult, setRebuildResult] = useState<string | null>(null);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-ink">Data Health</h1>
        <div className="flex gap-2">
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={isFetching}
            onClick={() => {
              void refetch();
            }}
          >
            Re-run invariants
          </button>
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={rebuildMutation.isPending}
            onClick={() => {
              rebuildMutation.mutate(undefined, {
                onSuccess: (res) => {
                  setRebuildResult(
                    res.matches
                      ? `Verified — replayed ${String(res.eventCount)} events, no drift found.`
                      : `Drift detected across ${String(res.eventCount)} events — restart the app to resync.`,
                  );
                },
              });
            }}
          >
            Rebuild projections
          </button>
        </div>
      </div>

      {rebuildResult !== null && <p className="mb-3 text-sm text-ink">{rebuildResult}</p>}

      {isLoading && <p className="text-sm text-muted">Checking…</p>}
      {isError && <p className="text-sm text-danger">Couldn’t run the health checks.</p>}

      {report?.sections.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-[var(--radius-card)] border border-border bg-surface p-10 text-center">
          <CircleCheck size={32} className="text-success" />
          <p className="text-sm font-medium text-ink">All invariants passing</p>
          <p className="text-xs text-muted">
            No duplicates, stale transfers, or balance drift to review.
          </p>
        </div>
      )}

      {report !== undefined && report.sections.length > 0 && (
        <>
          {!report.invariantsPassing && (
            <p className="mb-3 text-sm text-danger">
              Some hard invariants are failing — resolve the error sections first.
            </p>
          )}
          {report.sections.map((section) => (
            <Section
              key={section.id}
              section={section}
              transfersById={transfersById}
              onFixed={onFixed}
            />
          ))}
        </>
      )}
    </div>
  );
}
